import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Box,
    Typography,
    Avatar,
    useMediaQuery,
} from "@mui/material";
import { Star } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import { TIER_DISPLAY, TIERS } from "../../constants/membership";
import Rating3Badge from "../common/Rating3Badge";
import ImpactRatingBadge from "../common/ImpactRatingBadge";
import { FOUNDER_BADGE, FOUNDER_TOOLTIP_TEXT } from "../../constants/founder";
import { toProfilePath } from "../../universal/v2Routes";
import EloBadge from "../common/EloBadge.jsx";
import TooltipComponent from "../common/Tooltip";
import utils from "../../universal/utils";
import {
    pickAvatarUpdatedAtMs,
    resolveAvatarSrc,
} from "../../utils/avatarUrl";
import { isPendingRequestForRecipient } from "../../utils/friendRequests";
import { findFriendRecordForPlayer } from "../../utils/friendIdentity";
import { resolveLobbyPlayerOnlineState } from "../../utils/lobbyPresence";
import { MembershipBadge } from "../profile/profileIdentityBadges";
import { LOBBY_PARTY_CARD_SX, MOBILE_NAV_MQ } from "./lobbyPageStyles";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import LobbyPlayerMobileRow from "./LobbyPlayerMobileRow";
import LobbyPlayerPreviewCard from "./LobbyPlayerPreviewCard";

const FLAG_BADGE_STYLES = {
    position: "absolute",
    right: "1px",
    bottom: "0px",
    width: { xs: "1rem", xl: "1.2rem" },
    height: { xs: "1rem", xl: "1.2rem" },
    borderRadius: "50%",
    border: `2px solid ${alpha(colors.background.surface, 0.4)}`,
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.45), 0 0 0 2px rgba(91, 33, 230, 0.25)",
    backgroundColor: alpha(colors.background.surface, 0.8),
    objectFit: "cover",
};

const FOUNDER_AVATAR_BADGE_STYLES = {
    position: "absolute",
    top: { xs: "-3px", xl: "-4px" },
    left: { xs: "-3px", xl: "-4px" },
    display: "flex",
    alignItems: "center",
    gap: "0.1rem",
    backgroundColor: "rgba(28, 30, 32, 0.92)",
    border: `1px solid ${FOUNDER_BADGE.borderColor}`,
    borderRadius: "8px",
    padding: { xs: "0.05rem 0.25rem", xl: "0.1rem 0.3rem" },
    zIndex: 1,
    cursor: "default",
};

const FOUNDER_AVATAR_ICON_STYLES = {
    color: FOUNDER_BADGE.color,
    fontSize: { xs: "0.6rem", xl: "0.7rem" },
};

const FOUNDER_AVATAR_TEXT_STYLES = {
    color: FOUNDER_BADGE.color,
    fontSize: { xs: "0.45rem", xl: "0.55rem" },
    fontWeight: 700,
    letterSpacing: "0.3px",
    lineHeight: 1,
};

const ELO_CONTAINER_STYLES = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
};

const BADGE_ROW_STYLES = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.25rem",
    flexWrap: "wrap",
    marginTop: "0.2rem",
    flexShrink: 0,
};

function getMembershipBadgeRowSx(hasRatingBadges) {
    return {
        ...BADGE_ROW_STYLES,
        marginTop: hasRatingBadges ? "0.2rem" : "0.55rem",
    };
}

const PLAYER_AVATAR_WRAP_SX = {
    position: "relative",
    width: { xs: "4rem", xl: "5.4rem" },
    aspectRatio: "1 / 1",
    margin: "0.55rem auto",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    background: `
        radial-gradient(
            circle at top,
            rgba(246,185,76,0.16),
            rgba(255,255,255,0.03)
        )
    `,
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.06),
        0 10px 24px rgba(0,0,0,0.35),
        0 0 30px rgba(246,185,76,0.08)
    `,
    transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
};

/** Main/self player — keep avatar hero-sized inside the shared card frame. */
const SELF_AVATAR_WRAP_SX = {
    ...PLAYER_AVATAR_WRAP_SX,
    width: { xs: "5rem", xl: "6.5rem" },
    margin: "0.7rem auto",
};

const LOBBY_AVATAR_SX = {
    position: "relative",
    width: "90%",
    height: "90%",
    minWidth: 0,
    minHeight: 0,
    maxWidth: "100%",
    maxHeight: "100%",
    borderRadius: "50% !important",
    aspectRatio: "1 / 1",
    border: "2px solid rgba(255,255,255,0.08)",
    boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.42)",
    "& .MuiAvatar-img": {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
        borderRadius: "50% !important",
    },
    "& .MuiSvgIcon-root": {
        width: "56%",
        height: "56%",
        transform: "translateY(-4%)",
    },
};

const LOBBY_PLAYER_FLIP_CARD_SX = {
    ...LOBBY_PARTY_CARD_SX,
};

/** Sits on the card rim; must render outside overflow:hidden / filter faces. */
const OFFLINE_STATUS_PILL_SX = {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 40,
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    padding: "0.38rem 0.9rem",
    borderRadius: "999px",
    pointerEvents: "none",
    background: `
        linear-gradient(
            180deg,
            rgba(32,32,36,0.98) 0%,
            rgba(18,18,20,1) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: `
        0 10px 30px rgba(0,0,0,0.45),
        0 0 18px rgba(0,0,0,0.25),
        inset 0 1px 0 rgba(255,255,255,0.05)
    `,
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        borderRadius: "inherit",
        padding: "1px",
        background: `
            linear-gradient(
                90deg,
                transparent,
                rgba(255,255,255,0.12),
                transparent
            )
        `,
        WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
        WebkitMaskComposite: "xor",
        pointerEvents: "none",
    },
};

const OFFLINE_STATUS_DOT_SX = {
    width: "0.45rem",
    height: "0.45rem",
    borderRadius: "50%",
    backgroundColor: "#8e8e8e",
    boxShadow: "0 0 10px rgba(180,180,180,0.45)",
};

const OFFLINE_STATUS_TEXT_SX = {
    fontSize: "0.62rem",
    fontWeight: 800,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.74)",
    lineHeight: 1,
};

const basePath = utils.getEdgeBasePath();

function selectCatalog(state) {
    return state.catalogReducer ?? state.catalog ?? {};
}

function findLiveLobbyMember(currentLobby, userId) {
    if (userId == null || !currentLobby?.members?.length) return null;
    const uid = String(userId);
    return currentLobby.members.find((m) => m && String(m.userId) === uid) ?? null;
}

function mergePlayerPresenceFromLobbyRow(player, live) {
    if (!player) return player;
    if (!live) return player;
    return {
        ...player,
        isOnline: live.isOnline !== undefined ? live.isOnline : player.isOnline,
        playerStatus: live.playerStatus ?? player.playerStatus,
        lastOnlineUpdate: live.lastOnlineUpdate ?? player.lastOnlineUpdate,
    };
}

function resolveMembershipDisplay(player) {
    const tier = (player?.membershipTier || player?.membership?.tier || TIERS.FREE)
        .toString()
        .trim()
        .toLowerCase();

    if (tier === TIERS.FREE) return null;

    const display = TIER_DISPLAY[tier] || TIER_DISPLAY[TIERS.FREE];
    if (!display?.badge) return null;

    return { label: display.badge, color: display.color };
}

function resolveFounderBadge(player) {
    return player?.founderBadge?.granted ? player.founderBadge : null;
}

function renderLobbyPlayerName({ isLobbyHost, nickname, emphasize = false }) {
    return (
        <>
            {isLobbyHost && (
                <TooltipComponent title="Leader">
                    <Star
                        sx={{
                            color: colors.primary.main,
                            fontSize: emphasize
                                ? { xs: "1.25rem", xl: "1.4rem" }
                                : { xs: "1.15rem", xl: "1.25rem" },
                            filter: `drop-shadow(0 2px 4px ${alpha(colors.primary.main, 0.6)})`,
                            flexShrink: 0,
                        }}
                    />
                </TooltipComponent>
            )}
            <Typography
                title={nickname}
                sx={{
                    display: "block",
                    position: "relative",
                    color: colors.text.primary,
                    fontSize: emphasize
                        ? { xs: "1.15rem", xl: "1.3rem" }
                        : { xs: "1.05rem", xl: "1.2rem" },
                    textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    textRendering: "geometricPrecision",
                    WebkitFontSmoothing: "antialiased",
                    minWidth: 0,
                    maxWidth: "100%",
                    px: "0.15rem",
                }}
            >
                {nickname}
            </Typography>
        </>
    );
}

export default function LobbyPlayerBox({
    player = null,
    isYou = false,
    onKick,
    friendlist = [],
    handleSendRequestFriend,
    pendingRequests = [],
    lobbyState,
    currentLobbyCode,
    isCurrentUserLeader = false,
    canFlipCards = true,
    showUnavailableActionMessage,
    avatarCacheBust = 0,
}) {
    const history = useHistory();
    const location = useLocation();
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);
    const avatarAnchorRef = useRef(null);
    const [playerMenuOpen, setPlayerMenuOpen] = useState(false);

    const catalogSlice = useSelector(selectCatalog);
    const currentLobby = catalogSlice.currentLobby;
    const friendsFromStore = catalogSlice.friends;

    const liveLobbyMember = useMemo(
        () => findLiveLobbyMember(currentLobby, player?.userId),
        [currentLobby, player?.userId],
    );

    const playerForPresence = useMemo(
        () => mergePlayerPresenceFromLobbyRow(player, liveLobbyMember),
        [player, liveLobbyMember],
    );

    const friendRecord = useMemo(
        () =>
            findFriendRecordForPlayer(friendsFromStore, player) ||
            findFriendRecordForPlayer(friendlist, player),
        [friendsFromStore, friendlist, player],
    );

    const isLobbyHost = player && player.role === "host";
    const isSearching = lobbyState === "searching";
    const showSearchingLayer = isSearching || (!canFlipCards && currentLobbyCode);

    const avatarVersion = pickAvatarUpdatedAtMs(
        player,
        isYou ? avatarCacheBust : null
    );

    const avatarSrc = resolveAvatarSrc({
        basePath,
        picture: player?.picture,
        userId: player?.userId || player?._id,
        avatarUpdatedAtMs: avatarVersion,
        hasCustomAvatar: !!player?.avatar,
    });
    const [avatarDisplaySrc, setAvatarDisplaySrc] = useState(avatarSrc);

    useEffect(() => {
        setAvatarDisplaySrc(avatarSrc || null);
    }, [avatarSrc]);

    const handleAvatarError = () => {
        if (avatarDisplaySrc && player?.picture && avatarDisplaySrc !== player.picture) {
            setAvatarDisplaySrc(player.picture);
            return;
        }
        if (avatarDisplaySrc) {
            setAvatarDisplaySrc(null);
        }
    };

    const isFriend = !!friendRecord;
    const playerSteamId =
        player?.steamId || player?.steamId64 || player?.steam64Id || null;

    const isPlayerOnline = resolveLobbyPlayerOnlineState({
        player: playerForPresence,
        friendRecord,
    });

    const appearsDisconnected = isPlayerOnline !== true;

    const hasPendingRequest =
        Boolean(playerSteamId) && Array.isArray(pendingRequests)
            ? pendingRequests.some((req) =>
                isPendingRequestForRecipient(req, playerSteamId)
            )
            : false;

    const founderBadge = resolveFounderBadge(player);
    const membershipDisplay = resolveMembershipDisplay(player);
    const hasBadges = !!membershipDisplay;
    const rating3Value = player?.rating3?.current ?? player?.rating3;
    const rating3SubRatings = player?.rating3?.subRatings || player?.rating3Sub || null;
    const impactRatingValue = player?.impactRating?.current ?? null;
    const impactRatingComponents = player?.impactRating?.components || null;
    const hasRatingBadges = rating3Value != null || impactRatingValue != null;

    const handleProfile = () => {
        setPlayerMenuOpen(false);
        const profilePath = toProfilePath(player?._id, location.pathname);
        if (profilePath) {
            history.push(profilePath);
        }
    };

    const togglePlayerMenu = () => {
        if (canFlipCards === false) {
            showUnavailableActionMessage?.();
            return;
        }
        setPlayerMenuOpen((current) => !current);
    };

    useEffect(() => {
        if (!playerMenuOpen) return undefined;

        const handlePointerDown = (event) => {
            if (avatarAnchorRef.current?.contains(event.target)) return;
            if (event.target.closest?.("[data-lobby-player-menu]")) return;
            setPlayerMenuOpen(false);
        };
        const handleKeyDown = (event) => {
            if (event.key !== "Escape") return;
            setPlayerMenuOpen(false);
            avatarAnchorRef.current?.focus();
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [playerMenuOpen]);

    if (isMobile) {
        return (
            <>
                <LobbyPlayerMobileRow
                    ref={avatarAnchorRef}
                    player={player}
                    isYou={isYou}
                    isLobbyHost={isLobbyHost}
                    appearsDisconnected={appearsDisconnected}
                    showSearchingLayer={showSearchingLayer}
                    avatarDisplaySrc={avatarDisplaySrc}
                    onAvatarError={handleAvatarError}
                    founderBadge={founderBadge}
                    previewOpen={playerMenuOpen}
                    onOpenPreview={!isYou ? togglePlayerMenu : undefined}
                />
                {!isYou ? (
                    <LobbyPlayerPreviewCard
                        open={playerMenuOpen}
                        anchorEl={avatarAnchorRef.current}
                        player={player}
                        avatarSrc={avatarDisplaySrc}
                        isOnline={isPlayerOnline === true}
                        playerStatus={playerForPresence?.playerStatus}
                        isFriend={isFriend}
                        hasPendingRequest={hasPendingRequest}
                        canAddFriend={
                            typeof handleSendRequestFriend === "function"
                        }
                        pendingRequests={pendingRequests}
                        canKick={
                            Boolean(canFlipCards) &&
                            isCurrentUserLeader &&
                            typeof onKick === "function"
                        }
                        onProfile={handleProfile}
                        onKick={() => {
                            setPlayerMenuOpen(false);
                            onKick(player);
                        }}
                        onAddFriend={(targetSteamId) => {
                            if (!targetSteamId) return;
                            setPlayerMenuOpen(false);
                            handleSendRequestFriend(targetSteamId);
                        }}
                        onClose={() => setPlayerMenuOpen(false)}
                    />
                ) : null}
            </>
        );
    }

    if (isYou) {
        return (
            <Box
                sx={{
                    // Hero/self card — intentionally larger than side slots
                    width: { xs: "10.7rem", xl: "12.9rem" },
                    height: { xs: "14.6rem", xl: "17rem" },
                    flexShrink: 0,
                    boxSizing: "border-box",
                    position: "relative",
                    isolation: "isolate",
                    borderRadius: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: { xs: "1rem", xl: "1.15rem" },
                    background: `
                        linear-gradient(
                            180deg,
                            rgba(20,22,26,0.96) 0%,
                            rgba(11,13,16,0.98) 100%
                        )
                    `,
                    border: "1px solid rgba(255,255,255,0.06)",
                    backdropFilter: "blur(12px)",
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,0.04),
                        inset 0 -1px 0 rgba(0,0,0,0.35),
                        0 20px 52px rgba(0,0,0,0.52)
                    `,
                    transition: `
                        box-shadow 0.15s ease-in-out,
                        border-color 0.15s ease-in-out
                    `,
                    [MOBILE_NAV_MQ]: {
                        width: "12.2rem",
                        height: "15rem",
                        maxWidth: "100%",
                        borderRadius: 0,
                        padding: "0.9rem",
                    },
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "8%",
                        width: "84%",
                        height: "1px",
                        background: `
                            linear-gradient(
                                90deg,
                                transparent 0%,
                                rgba(246,185,76,0.85) 50%,
                                transparent 100%
                            )
                        `,
                        opacity: 0.9,
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        background: `
                            radial-gradient(
                                circle at top,
                                rgba(246,185,76,0.08) 0%,
                                transparent 60%
                            )
                        `,
                        pointerEvents: "none",
                    },
                    "& .cardNoise": {
                        position: "absolute",
                        inset: 0,
                        opacity: 0.18,
                        backgroundImage: `
                            repeating-linear-gradient(
                                0deg,
                                rgba(255,255,255,0.025) 0px,
                                rgba(255,255,255,0.025) 1px,
                                transparent 2px,
                                transparent 5px
                            )
                        `,
                        pointerEvents: "none",
                        zIndex: 0,
                    },
                    "& .cardBorder": {
                        position: "absolute",
                        inset: 0,
                        borderRadius: "inherit",
                        padding: "1px",
                        background: `
                            linear-gradient(
                                135deg,
                                rgba(246,185,76,0.28),
                                transparent 28%,
                                transparent 72%,
                                rgba(246,185,76,0.12)
                            )
                        `,
                        WebkitMask:
                            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                        WebkitMaskComposite: "xor",
                        pointerEvents: "none",
                        zIndex: 3,
                    },
                }}
            >

                <Box className="cardNoise" />
                <Box className="cardBorder" />

                {showSearchingLayer && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            borderRadius: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: alpha(colors.background.card, 0.44),
                            zIndex: 10,
                        }}
                    />
                )}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.2rem",
                        width: "100%",
                        minWidth: 0,
                        flexShrink: 0,
                    }}
                >
                    {renderLobbyPlayerName({
                        isLobbyHost,
                        nickname: player.nickname,
                        emphasize: true,
                    })}
                </Box>
                <Box sx={SELF_AVATAR_WRAP_SX}>
                    <Avatar
                        src={avatarDisplaySrc}
                        onError={handleAvatarError}
                        sx={{
                            ...LOBBY_AVATAR_SX,
                        }}
                    />
                    {player.flagImageUrl && (
                        <Box
                            component="img"
                            sx={FLAG_BADGE_STYLES}
                            src={player.flagImageUrl}
                            alt=""
                            aria-hidden="true"
                        />
                    )}
                    {founderBadge && (
                        <TooltipComponent title={FOUNDER_TOOLTIP_TEXT} placement="top" arrow>
                            <Box sx={FOUNDER_AVATAR_BADGE_STYLES}>
                                <Star sx={FOUNDER_AVATAR_ICON_STYLES} />
                                <Box component="span" sx={FOUNDER_AVATAR_TEXT_STYLES}>
                                    #{founderBadge.number || ""}
                                </Box>
                            </Box>
                        </TooltipComponent>
                    )}
                </Box>
                <Box sx={ELO_CONTAINER_STYLES}>
                    <EloBadge value={player.elo} size="small" />
                </Box>
                {rating3Value != null && (
                    <Rating3Badge
                        value={rating3Value}
                        subRatings={rating3SubRatings}
                        size="small"
                        showLabel
                        compact
                    />
                )}
                {impactRatingValue != null && (
                    <ImpactRatingBadge
                        value={impactRatingValue}
                        components={impactRatingComponents}
                        size="small"
                        showLabel
                        compact
                    />
                )}
                {hasBadges && (
                    <Box sx={getMembershipBadgeRowSx(hasRatingBadges)}>
                        {membershipDisplay && (
                            <MembershipBadge
                                label={membershipDisplay.label}
                                size="compact"
                            />
                        )}
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                ...LOBBY_PLAYER_FLIP_CARD_SX,
                position: "relative",
                cursor: "default",
                flexShrink: 0,
                overflow: "visible",
            }}
        >
            {appearsDisconnected && (
                <Box sx={OFFLINE_STATUS_PILL_SX}>
                    <Box sx={OFFLINE_STATUS_DOT_SX} />
                    <Typography sx={OFFLINE_STATUS_TEXT_SX}>Offline</Typography>
                </Box>
            )}
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            borderRadius: 0,
                            position: "relative",
                            transition:
                                "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                            background: `
                                linear-gradient(
                                    180deg,
                                    rgba(24, 26, 31, 0.98) 0%,
                                    rgba(14, 16, 20, 0.985) 55%,
                                    rgba(10, 12, 16, 1) 100%
                                )
                            `,
                            boxShadow: `
                                0 24px 70px rgba(0,0,0,0.62),
                                0 0 0 1px rgba(255,255,255,0.02),
                                inset 0 1px 0 rgba(255,255,255,0.05),
                                inset 0 -12px 24px rgba(0,0,0,0.22)
                            `,
                            display: "flex",
                            flexDirection: "column",
                            backdropFilter: "blur(18px)",
                            WebkitBackdropFilter: "blur(18px)",
                            border: `1px solid ${colors.border.default}`,
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 0,
                            overflow: "hidden",
                            maxHeight: "100%",
                            boxSizing: "border-box",
                            padding: { xs: "0.55rem", xl: "0.65rem" },
                            cursor: "default",
                            filter: appearsDisconnected
                                ? "grayscale(0.72) brightness(0.72)"
                                : "none",

                            opacity: appearsDisconnected ? 0.82 : 1,
                            isolation: "isolate",
                            "&:hover": {
                                borderColor: "rgba(246,185,76,0.35)",
                                boxShadow: `
                                    0 28px 72px rgba(0,0,0,0.68),
                                    0 0 28px rgba(246,185,76,0.1),
                                    inset 0 1px 0 rgba(255,255,255,0.06)
                                `,
                            },
                            "&:hover .playerGlow": {
                                opacity: 1,
                            },
                            "&:hover .playerNoise": {
                                opacity: 0.09,
                            },
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                height: "1px",
                                background: `
                                    linear-gradient(
                                        90deg,
                                        transparent,
                                        rgba(246,185,76,0.7),
                                        transparent
                                    )
                                `,
                                opacity: 0.9,
                            },
                            "&:hover .shineSweep": {
                                transform: "translateX(140%)",
                            },
                        }}
                    >
                        {/* Clipping layer */}
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                borderRadius: 0,
                                overflow: "hidden",
                                zIndex: 0,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                            className="cardClipLayer"
                        >
                            <Box
                                className="shineSweep"
                                sx={{
                                    position: "absolute",
                                    inset: "-40%",
                                    background: `
                                        linear-gradient(
                                            115deg,
                                            transparent 30%,
                                            rgba(255,255,255,0.045) 50%,
                                            transparent 70%
                                        )
                                    `,
                                    transform: "translateX(-140%)",
                                    transition:
                                        "transform 1200ms cubic-bezier(0.22, 1, 0.36, 1)",
                                    pointerEvents: "none",
                                }}
                            />
                            <Box
                                className="playerGlow"
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    borderRadius: "inherit",
                                    background: `
                                    radial-gradient(
                                        circle at top,
                                        rgba(246,185,76,0.12) 0%,
                                        transparent 55%
                                    )
                                `,
                                    opacity: 0.45,
                                    transition: "opacity 260ms ease",
                                    pointerEvents: "none",
                                }}
                            />

                            <Box
                                className="playerNoise"
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    opacity: 0.04,
                                    backgroundImage:
                                        "repeating-linear-gradient(0deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 2px, transparent 4px)",
                                    pointerEvents: "none",
                                    transition: "opacity 260ms ease",
                                }}
                            />

                            {appearsDisconnected && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        borderRadius: "inherit",
                                        background: `
                                        radial-gradient(
                                            circle at center,
                                            transparent 35%,
                                            rgba(0,0,0,0.38) 100%
                                        )
                                    `,
                                        pointerEvents: "none",
                                        zIndex: 2,
                                    }}
                                />
                            )}
                            {showSearchingLayer && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        borderRadius: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: alpha(colors.background.card, 0.44),
                                        zIndex: 10,
                                        pointerEvents: "none",
                                    }}
                                />
                            )}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "0.2rem",
                                    width: "100%",
                                    minWidth: 0,
                                    flexShrink: 0,
                                }}
                            >
                                {renderLobbyPlayerName({
                                    isLobbyHost,
                                    nickname: player.nickname,
                                })}
                            </Box>
                            <Box
                                ref={avatarAnchorRef}
                                onClick={togglePlayerMenu}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        togglePlayerMenu();
                                    }
                                }}
                                tabIndex={0}
                                role="button"
                                aria-haspopup="dialog"
                                aria-expanded={playerMenuOpen}
                                aria-label={`Open ${player.nickname || "player"} menu`}
                                sx={{
                                    ...PLAYER_AVATAR_WRAP_SX,
                                    zIndex: 12,
                                    cursor: "pointer",
                                    outline: "none",
                                    border: appearsDisconnected
                                        ? "1px solid rgba(255,255,255,0.04)"
                                        : PLAYER_AVATAR_WRAP_SX.border,
                                    boxShadow: appearsDisconnected
                                        ? `
                                            0 0 0 1px rgba(255,255,255,0.02),
                                            0 0 20px rgba(0,0,0,0.4)
                                        `
                                        : PLAYER_AVATAR_WRAP_SX.boxShadow,
                                    "&:hover, &:focus-visible": {
                                        borderColor: alpha(colors.primary.main, 0.42),
                                        boxShadow: `
                                            inset 0 1px 0 rgba(255,255,255,0.06),
                                            0 10px 24px rgba(0,0,0,0.35),
                                            0 0 28px ${alpha(colors.primary.main, 0.16)}
                                        `,
                                    },
                                }}
                            >
                                <Avatar
                                    src={avatarDisplaySrc}
                                    onError={handleAvatarError}
                                    sx={{
                                        ...LOBBY_AVATAR_SX,
                                        filter: appearsDisconnected ? "grayscale(0.9) brightness(0.72)" : "none",
                                        opacity: appearsDisconnected ? 0.82 : 1,
                                    }}
                                />
                                {player.flagImageUrl && isPlayerOnline === true && (
                                    <Box
                                        component="img"
                                        sx={FLAG_BADGE_STYLES}
                                        src={player.flagImageUrl}
                                        alt=""
                                        aria-hidden="true"
                                    />
                                )}
                                {founderBadge && (
                                    <TooltipComponent title={FOUNDER_TOOLTIP_TEXT} placement="top" arrow>
                                        <Box sx={FOUNDER_AVATAR_BADGE_STYLES}>
                                            <Star sx={FOUNDER_AVATAR_ICON_STYLES} />
                                            <Box component="span" sx={FOUNDER_AVATAR_TEXT_STYLES}>
                                                #{founderBadge.number || ""}
                                            </Box>
                                        </Box>
                                    </TooltipComponent>
                                )}
                            </Box>
                            <Box sx={ELO_CONTAINER_STYLES}>
                                <EloBadge value={player.elo} size="small" />
                            </Box>
                            {rating3Value != null && (
                                <Rating3Badge
                                    value={rating3Value}
                                    subRatings={rating3SubRatings}
                                    size="small"
                                    showLabel
                                    compact
                                />
                            )}
                            {impactRatingValue != null && (
                                <ImpactRatingBadge
                                    value={impactRatingValue}
                                    components={impactRatingComponents}
                                    size="small"
                                    showLabel
                                    compact
                                />
                            )}
                            {hasBadges && (
                                <Box sx={getMembershipBadgeRowSx(hasRatingBadges)}>
                                    {membershipDisplay && (
                                        <MembershipBadge
                                            label={membershipDisplay.label}
                                            size="compact"
                                        />
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
            <LobbyPlayerPreviewCard
                open={playerMenuOpen}
                anchorEl={avatarAnchorRef.current}
                player={player}
                avatarSrc={avatarDisplaySrc}
                isOnline={isPlayerOnline === true}
                playerStatus={playerForPresence?.playerStatus}
                isFriend={isFriend}
                hasPendingRequest={hasPendingRequest}
                canAddFriend={
                    typeof handleSendRequestFriend === "function"
                }
                pendingRequests={pendingRequests}
                canKick={
                    Boolean(canFlipCards) &&
                    isCurrentUserLeader &&
                    typeof onKick === "function"
                }
                onProfile={handleProfile}
                onKick={() => {
                    setPlayerMenuOpen(false);
                    onKick(player);
                }}
                onAddFriend={(targetSteamId) => {
                    if (!targetSteamId) return;
                    setPlayerMenuOpen(false);
                    handleSendRequestFriend(targetSteamId);
                }}
                onClose={() => setPlayerMenuOpen(false)}
            />
        </Box>
    );
}
